import type { CashflowBucket, CashflowFormState } from './types'

import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { min } from 'date-fns/min'
import { parseISO } from 'date-fns/parseISO'

import { DateFormat } from '@/utils/dates/dateFormatting'

import { spacedWindowMonths } from './granularity'
import { CashflowFrequency, CashflowFrequencyMap } from './types'

// Neutral fill for cash in a gap month: shown, but neither met nor short.
export const GAP_CASH_COLOR = 'var(--color-chart-neutral)'

type GapSchedule = Pick<
  CashflowFormState,
  'cashflowFrequency' | 'granularity' | 'startDate' | 'endDate'
>

function emptyMonth(start: Date, end: Date): CashflowBucket {
  return {
    start_date: format(start, DateFormat.ISO_DATE),
    end_date: format(end, DateFormat.ISO_DATE),
    amount: 0,
    start_date_formatted: '',
    coupon_amount: 0,
    principal_amount: 0,
    dividend_amount: 0,
    total_amount: 0,
    isGap: true,
  }
}

// Same rule as createCashflowBucket: each calendar year's first slot shows the year.
export function labelByYear(slots: CashflowBucket[]): CashflowBucket[] {
  let labelledYear: number | null = null
  return slots.map((slot) => {
    const start = parseISO(slot.start_date ?? slot.end_date)
    const showYear = start.getFullYear() !== labelledYear
    labelledYear = start.getFullYear()
    const pattern = showYear
      ? DateFormat.MONTH_ABBREV_YEAR_ABBREV
      : DateFormat.MONTH_ABBREV
    return { ...slot, start_date_formatted: format(start, pattern) }
  })
}

/** Puts an empty month between a spaced target's windows so charts show the gaps. */
export function withGapMonths(
  windows: CashflowBucket[],
  schedule: GapSchedule,
): CashflowBucket[] {
  const { cashflowFrequency, granularity, startDate, endDate } = schedule
  if (cashflowFrequency === CashflowFrequency.OneTime || startDate === null) {
    return windows
  }
  const windowLength = spacedWindowMonths({
    frequency: cashflowFrequency,
    granularity,
  })
  if (windowLength === null) return windows

  const period = CashflowFrequencyMap[cashflowFrequency]
  const start = parseISO(startDate)
  const end = parseISO(endDate)
  const slots: CashflowBucket[] = []
  windows.forEach((bucket, index) => {
    slots.push(bucket)
    for (let offset = windowLength; offset < period; offset++) {
      const gapStart = addMonths(start, index * period + offset)
      if (gapStart >= end) break
      const gapEnd = min([addMonths(start, index * period + offset + 1), end])
      slots.push(emptyMonth(gapStart, gapEnd))
    }
  })
  return labelByYear(slots)
}
